import { Box, useTheme } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/special/Header';
import { useTranslation } from 'react-i18next';
import useFetch from '../../api/useFetch';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useEffect, useState } from 'react';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import { hasPermissions } from '../../auth/hasPermissions';
import { ExoSkeleton } from '../../components/exo/ExoSkeleton';
import { Toolbar } from '../../components/exo/project/Toolbar';
import { NewSalesObject } from '../../components/new/NewSalesObject';
import ExoDialog from '../../components/exo/ExoDialog';
import { tokens } from '../../global/theme/tokens';
import ExoTabs from '../../components/exo/ExoTabs';
import { SalesObjectParameterTab } from './tabs/SalesObjectParameterTab';
import { SalesObjectLevelTab } from './tabs/SalesObjectLevelTab';
import SaveIcon from '@mui/icons-material/Save';
import { NewSalesObjectPreset } from './NewSalesObjectPreset';
import { SalesObjectCustomerPreview } from './tabs/SalesObjectCustomerPreview';
import { SalesObjectCustomerMatching } from './tabs/SalesObjectCustomerMatching';
import { SalesObjectActiveLeads } from './tabs/SalesObjectActiveLeadsTab';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LayersIcon from '@mui/icons-material/Layers';
import EditIcon from '@mui/icons-material/Edit';

export const ShowSalesObject = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const { apiData, setApiData } = useFetch(`sales-objects/${id}`);
  const [open, setOpen] = useState(false);
  const [openSaveAsPreset, setOpenSaveAsPreset] = useState(false);

  // object management
  const [inEditMode, setInEditMode] = useState(false);
  const toollist = [
    {
      Icon: SettingsApplicationsIcon,
      label: t('Settings'),
      onClick: () => setOpen(true),
      hidden: !hasPermissions('sales-object', 2),
    },
    {
      Icon: BarChartIcon,
      label: t('Analytics'),
      onClick: () => navigate(`/sales-objects/${id}/statistics`),
      hidden: true || !hasPermissions('sales-object-statistics', 2),
    },
    {
      Icon: SaveIcon,
      label: t('Save as preset'),
      onClick: () => setOpenSaveAsPreset(true),
      hidden: !hasPermissions('sales-object', 2),
    },
  ];

  function handlePropChange(newProps) {
    setApiData({ ...apiData, data: { ...apiData.data, properties: newProps } });
  }

  function handleLevelPropChange(newProps, newProp, levelId) {
    setApiData({ ...apiData, data: { ...apiData.data, properties: newProps } });
  }

  const { title, image, owner, supervisor, levels, properties, status, nr } =
    apiData?.data || {};

  useEffect(() => {
    if (levels && levels.length === 0) setInEditMode(true);
  }, [apiData]);

  if (!apiData)
    return (
      <>
        <ExoSkeleton size={'38px'} />
      </>
    );

  const tabs = [
    {
      label: (
        <Box className="flex gap-2 items-center">
          <EditIcon />
          {t('master data')}
        </Box>
      ),
      content: (
        <SalesObjectParameterTab
          salesObjectId={id}
          properties={properties}
          setApiData={setApiData}
          onPropChange={handlePropChange}
          inEditMode={inEditMode}
          supervisor={supervisor}
          owner={owner}
        />
      ),
    },
    {
      label: (
        <Box className="flex gap-2 items-center">
          <LayersIcon />
          {t('sales levels')}
        </Box>
      ),
      content: (
        <SalesObjectLevelTab
          salesObjectId={id}
          apiData={apiData}
          setApiData={setApiData}
          inEditMode={inEditMode}
          onPropsChange={handleLevelPropChange}
        />
      ),
    },
    {
      label: (
        <Box className="flex gap-2 items-center">
          <AutoAwesomeIcon />
          {t('customer matching')}
        </Box>
      ),
      content: <SalesObjectCustomerMatching salesObjectId={id} />,
    },
    {
      label: (
        <Box className="flex gap-2 items-center">
          <VisibilityIcon />
          {t('customer preview')}
        </Box>
      ),
      content: <SalesObjectCustomerPreview salesObject={apiData?.data} />,
    },
    {
      label: (
        <Box className="flex gap-2 items-center">
          <RocketLaunchIcon />
          {t('active leads')}
        </Box>
      ),
      content: <SalesObjectActiveLeads salesObjectId={id} />,
    },
  ];

  return (
    <>
      <Header
        title={
          <>
            {image && (
              <img
                className=" h-10 w-20 object-cover rounded-[10px]"
                src={image.temporaryUrl}
              />
            )}
            {title}
          </>
        }
      />
      {hasPermissions('sales-object', 2) && (
        <Toolbar
          tools={toollist}
          setInEditMode={setInEditMode}
          inEditMode={inEditMode}
        />
      )}
      <Box className="flex flex-col h-full pt-1">
        <ExoTabs tabs={tabs} />
      </Box>
      <ExoDialog open={open} limitWidth onClose={() => setOpen(false)}>
        <NewSalesObject
          startData={apiData.data}
          onUpdate={(data) => {
            setOpen(false);
            setApiData(data);
          }}
          onClose={() => setOpen(false)}
        />
      </ExoDialog>
      <ExoDialog
        open={openSaveAsPreset}
        limitWidth
        onClose={() => setOpenSaveAsPreset(false)}
      >
        <NewSalesObjectPreset
          salesObjectId={id}
          onClose={() => setOpenSaveAsPreset(false)}
        />
      </ExoDialog>
    </>
  );
};
